import React, { useEffect, useState } from "react";
import { Plus, Save, Trash2, Upload, Copy, Monitor } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Panel } from "@/pages/shared";
import PretranslateButton from "@/components/PretranslateButton";
import { useCamp, logoUrl } from "@/context/SettingsContext";
import { LOGO_SIZES } from "@/lib/app";
import { printInfo } from "@/lib/printInfo";
import { EMPTY_HOURS, ddmmToKey, keyToDdmm } from "@/lib/stationery";

const DAY_KEYS = ["day_mon", "day_tue", "day_wed", "day_thu", "day_fri", "day_sat", "day_sun"];

function HoursRows({ t, hours, onChange, prefix }) {
  return (
    <div className="space-y-1.5">
      {DAY_KEYS.map((dk, i) => (
        <div key={dk} className="grid grid-cols-[3rem_1fr_1fr_auto] gap-2 items-center" data-testid={`${prefix}-row-${i}`}>
          <span className="text-xs font-semibold text-muted-foreground">{t(dk)}</span>
          <Input type="time" value={hours[i]?.from || ""} disabled={!!hours[i]?.closed}
            onChange={(e) => onChange(i, "from", e.target.value)} data-testid={`${prefix}-from-${i}`} />
          <Input type="time" value={hours[i]?.to || ""} disabled={!!hours[i]?.closed}
            onChange={(e) => onChange(i, "to", e.target.value)} data-testid={`${prefix}-to-${i}`} />
          <label className="flex items-center gap-1.5 text-xs whitespace-nowrap">
            <input type="checkbox" checked={!!hours[i]?.closed} data-testid={`${prefix}-closed-${i}`}
              onChange={(e) => onChange(i, "closed", e.target.checked)} />
            {t("setup_closed")}
          </label>
        </div>
      ))}
    </div>
  );
}

const DOC_LANGS = ["nl", "es", "de", "en", "no", "da", "fr"];
const EMERGENCY_KEYS = ["police", "guardia", "ambulance", "reception", "doctor", "doctor_urgent"];

const TEXT_FIELDS = [
  ["camp_name", "setup_camp_name"],
  ["street", "setup_street"],
  ["postal_city", "setup_postal_city"],
  ["country", "setup_country"],
  ["phone", "setup_phone"],
  ["email", "setup_email"],
  ["website", "setup_website"],
  ["gps", "setup_gps"],
  ["vat_number", "setup_vat_number"],
];

const NUM_FIELDS = [
  ["vat_rate", "setup_vat_rate"],
  ["tourist_tax", "setup_tourist_tax"],
  ["currency", "setup_currency"],
  ["planning_months_back", "planning_months_back"],
  ["planning_months_ahead", "planning_months_ahead"],
  ["beschikbaar_dagen", "beschikbaar_dagen"],
];

function LogoUpload({ which, camp, onDone }) {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", "general");
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch("/settings", { [which === "tent" ? "logo_tent_file_id" : "logo_file_id"]: data.id });
      await onDone();
      toast.success(t("saved"));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3 border border-border rounded-lg p-3">
      <img src={logoUrl(camp, which)} alt="" className="h-12 w-auto max-w-[110px] object-contain" data-testid={`setup-logo-preview-${which}`} />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-foreground">{t(which === "tent" ? "setup_logo_tent" : "setup_logo")}</p>
        <p className="text-[11px] text-muted-foreground">PNG · {LOGO_SIZES[which]}</p>
      </div>
      <label className="ml-auto shrink-0">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold cursor-pointer hover:bg-muted">
          <Upload className="h-3.5 w-3.5" />{busy ? "…" : t("upload")}
        </span>
        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} data-testid={`setup-logo-input-${which}`} />
      </label>
    </div>
  );
}

export default function SetupPanel() {
  const { t, lang } = useLang();
  const { camp, reload } = useCamp();
  const [form, setForm] = useState(camp);
  const [infoLang, setInfoLang] = useState("nl");
  const [saving, setSaving] = useState(false);
  const [hostCamp, setHostCamp] = useState(null);

  useEffect(() => { setForm(camp); }, [camp]);

  // Welke camping hoort bij dit webadres? Het buitenscherm volgt altijd het webadres.
  useEffect(() => {
    api.get("/public/settings")
      .then(({ data }) => setHostCamp(data?.tenant_id ? data : null))
      .catch(() => setHostCamp(null));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const emergency = (form.emergency?.length ? form.emergency : EMERGENCY_KEYS.map((k) => ({ key: k, label: "", number: "" })));
  const defaults = printInfo(infoLang);
  const hours = form.opening_hours?.length === 7 ? form.opening_hours : EMPTY_HOURS;
  const setHour = (i, k, v) => set("opening_hours", hours.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const seasons = form.hour_seasons || [];
  const setSeason = (i, k, v) => set("hour_seasons", seasons.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const setSeasonHour = (i, d, k, v) => {
    const rows = seasons[i]?.hours?.length === 7 ? seasons[i].hours : EMPTY_HOURS;
    setSeason(i, "hours", rows.map((r, idx) => (idx === d ? { ...r, [k]: v } : r)));
  };  const infoItems = form.info_items?.[infoLang]?.length
    ? form.info_items[infoLang]
    : defaults.items.map(([title, body]) => [title, body]);

  const setEmergency = (i, k, v) => {
    const rows = emergency.map((r, idx) => (idx === i ? { ...r, [k]: v } : r));
    set("emergency", rows);
  };
  const setInfo = (i, pos, v) => {
    const rows = infoItems.map((r, idx) => (idx === i ? (pos === 0 ? [v, r[1]] : [r[0], v]) : r));
    set("info_items", { ...(form.info_items || {}), [infoLang]: rows });
  };

  const wrongCamp = !!(hostCamp && camp?.tenant_id && hostCamp.tenant_id !== camp.tenant_id);

  const displayUrl = form.display_key
    ? `${window.location.origin}/display/${camp?.tenant_id || ""}?key=${form.display_key}`
    : "";

  const toggleDemo = async (on) => {
    if (on) {
      setForm((f) => ({ ...f, demo_mode: true }));
      try { await api.patch("/settings", { demo_mode: true }); reload(); toast.success(t("setup_demo_on_ok")); }
      catch (e) { toast.error(apiError(e.response?.data?.detail)); }
      return;
    }
    try {
      const { data } = await api.get("/dummy/count");
      if (data.total > 0 && !window.confirm(t("setup_demo_purge_confirm").replace("{n}", data.total))) {
        setForm((f) => ({ ...f, demo_mode: true }));
        return;
      }
      if (data.total > 0) await api.post("/dummy/purge");
      await api.patch("/settings", { demo_mode: false });
      setForm((f) => ({ ...f, demo_mode: false }));
      reload();
      toast.success(`${t("setup_demo_off_ok")} (${data.total})`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const save = async () => {    setSaving(true);
    const payload = {
      ...TEXT_FIELDS.reduce((a, [k]) => ({ ...a, [k]: form[k] ?? "" }), {}),
      currency: form.currency || "EUR",
      vat_rate: Number(form.vat_rate) || 0,
      tourist_tax: Number(form.tourist_tax) || 0,
      planning_months_back: Number(form.planning_months_back) || 0,
      planning_months_ahead: Number(form.planning_months_ahead) || 12,
      beschikbaar_dagen: Math.min(30, Math.max(1, Number(form.beschikbaar_dagen) || 2)),
      default_lang: form.default_lang || "nl",
      translation_base_lang: form.translation_base_lang || "nl",
      login_style: form.login_style || "sober",
      reminder_days: Number(form.reminder_days) || 14,
      reminder_repeat_days: Math.min(90, Math.max(1, Number(form.reminder_repeat_days) || 7)),
      reminder_max: Math.min(20, Math.max(0, Number(form.reminder_max ?? 3))),
      reminder_enabled: form.reminder_enabled !== false,
      dunning_tier1_days: Math.min(365, Math.max(1, Number(form.dunning_tier1_days) || 15)),
      dunning_tier2_days: Math.min(365, Math.max(1, Number(form.dunning_tier2_days) || 30)),
      dunning_tier3_days: Math.min(365, Math.max(1, Number(form.dunning_tier3_days) || 45)),
      provisional_reminder_enabled: form.provisional_reminder_enabled !== false,
      provisional_reminder_days: Math.min(60, Math.max(1, Number(form.provisional_reminder_days) || 7)),
      review_delay_days: Number(form.review_delay_days) || 3,
      review_enabled: form.review_enabled !== false,
      stock_alert_hour: Math.min(23, Math.max(0, Number(form.stock_alert_hour ?? 18))),
      stock_alert_enabled: form.stock_alert_enabled !== false,
      overdue_reminder_enabled: form.overdue_reminder_enabled !== false,
      interim_reminder_enabled: form.interim_reminder_enabled !== false,
      interim_reminder_days: Math.min(180, Math.max(1, Number(form.interim_reminder_days) || 30)),
      interim_auto_invoice_enabled: form.interim_auto_invoice_enabled !== false,
      interim_auto_min_days: Math.min(180, Math.max(1, Number(form.interim_auto_min_days) || 14)),
      audit_retention_days: Math.min(3650, Math.max(1, Number(form.audit_retention_days) || 90)),
      demo_mode: form.demo_mode === true,
      intro_music: form.intro_music !== false,
      bank_name: form.bank_name ?? "",
      bank_account: form.bank_account ?? "",
      bank_bic: form.bank_bic ?? "",
      footer_extra: form.footer_extra ?? "",
      opening_hours: hours,
      hour_seasons: seasons.filter((s) => s.start && s.end),
      emergency,
      info_items: { ...(form.info_items || {}) },
      payment_mode: form.payment_mode === "deposit" ? "deposit" : "full",
      deposit_type: form.deposit_type === "fixed" ? "fixed" : "percent",
      deposit_value: Math.max(0, Number(form.deposit_value) || 0),
    };
    try {
      await api.patch("/settings", payload);
      await reload();
      toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Panel
      title={`${t("setup")}${camp?.camp_name ? ` · ${camp.camp_name}` : ""}`}
      testid="setup-panel"
      action={<Button onClick={save} disabled={saving} data-testid="setup-save"><Save className="h-4 w-4 mr-2" />{saving ? "…" : t("save")}</Button>}
    >
      <div className="p-5 space-y-6">
        <p className="text-sm text-muted-foreground">{t("setup_desc")}</p>

        <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3" data-testid="setup-payment">
          <p className="text-sm font-display font-bold text-foreground">{t("setup_payment_title")}</p>
          <p className="text-xs text-muted-foreground">{t("setup_payment_desc")}</p>
          <div className="flex flex-wrap gap-1.5">
            {[["full", "setup_payment_full"], ["deposit", "setup_payment_deposit"]].map(([v, key]) => (
              <button key={v} type="button" onClick={() => set("payment_mode", v)} data-testid={`setup-payment-mode-${v}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${(form.payment_mode || "full") === v ? "bg-secondary text-primary border-primary/40" : "border-border text-muted-foreground hover:bg-muted"}`}>{t(key)}</button>
            ))}
          </div>
          {form.payment_mode === "deposit" && (
            <div className="flex flex-wrap items-end gap-3 pt-1">
              <div className="space-y-1.5">
                <Label className="text-xs">{t("setup_deposit_type")}</Label>
                <div className="flex gap-1.5">
                  {[["percent", "%"], ["fixed", "€"]].map(([v, sym]) => (
                    <button key={v} type="button" onClick={() => set("deposit_type", v)} data-testid={`setup-deposit-type-${v}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${(form.deposit_type || "percent") === v ? "bg-secondary text-primary border-primary/40" : "border-border text-muted-foreground hover:bg-muted"}`}>{sym}</button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{form.deposit_type === "fixed" ? t("setup_deposit_amount") : t("setup_deposit_percent")}</Label>
                <div className="flex items-center gap-1">
                  <Input type="number" min="0" step={form.deposit_type === "fixed" ? "0.01" : "1"} className="w-28" value={form.deposit_value ?? 30}
                    onChange={(e) => set("deposit_value", Number(e.target.value))} data-testid="setup-deposit-value" />
                  <span className="text-sm text-muted-foreground">{form.deposit_type === "fixed" ? "€" : "%"}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TEXT_FIELDS.map(([k, key]) => (
            <div key={k} className="space-y-1.5">
              <Label htmlFor={`setup-${k}`} className="text-xs">{t(key)}</Label>
              <Input id={`setup-${k}`} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`setup-${k}`} />
            </div>
          ))}
          {NUM_FIELDS.map(([k, key]) => (
            <div key={k} className="space-y-1.5">
              <Label htmlFor={`setup-${k}`} className="text-xs">{t(key)}</Label>
              <Input id={`setup-${k}`} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`setup-${k}`} />
            </div>
          ))}
          <div className="space-y-1.5">
            <Label className="text-xs">{t("setup_default_lang")}</Label>
            <div className="flex gap-1.5">
              {["nl", "es", "en"].map((l) => (
                <button key={l} type="button" onClick={() => set("default_lang", l)} data-testid={`setup-lang-${l}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${form.default_lang === l ? "bg-secondary text-primary border-primary/40" : "border-border text-muted-foreground hover:bg-muted"}`}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("setup_base_lang")}</Label>
            <div className="flex gap-1.5">
              {["nl", "es", "en"].map((l) => (
                <button key={l} type="button" onClick={() => set("translation_base_lang", l)} data-testid={`setup-base-lang-${l}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${(form.translation_base_lang || "nl") === l ? "bg-secondary text-primary border-primary/40" : "border-border text-muted-foreground hover:bg-muted"}`}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">{t("setup_base_lang_hint")}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("pretranslate_label")}</Label>
            <PretranslateButton />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("setup_dunning")}</Label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.reminder_enabled !== false} data-testid="setup-reminder-enabled"
                onChange={(e) => set("reminder_enabled", e.target.checked)} />
              {t("setup_reminder_enabled")}
            </label>
            <p className="text-[11px] text-muted-foreground">{t("setup_dunning_hint")}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("setup_dunning_tiers")}</Label>
            <div className="flex flex-wrap items-center gap-2">
              <Input type="number" min="1" max="365" className="w-20" value={form.dunning_tier1_days ?? 15}
                onChange={(e) => set("dunning_tier1_days", Number(e.target.value))} data-testid="setup-dunning-tier1" />
              <Input type="number" min="1" max="365" className="w-20" value={form.dunning_tier2_days ?? 30}
                onChange={(e) => set("dunning_tier2_days", Number(e.target.value))} data-testid="setup-dunning-tier2" />
              <Input type="number" min="1" max="365" className="w-20" value={form.dunning_tier3_days ?? 45}
                onChange={(e) => set("dunning_tier3_days", Number(e.target.value))} data-testid="setup-dunning-tier3" />
            </div>
            <p className="text-[11px] text-muted-foreground">{t("setup_dunning_tiers_hint")}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("setup_provisional_reminder")}</Label>
            <div className="flex items-center gap-2">
              <Input type="number" min="1" max="60" className="w-24" value={form.provisional_reminder_days ?? 7}
                onChange={(e) => set("provisional_reminder_days", Number(e.target.value))} data-testid="setup-provisional-days" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.provisional_reminder_enabled !== false} data-testid="setup-provisional-enabled"
                  onChange={(e) => set("provisional_reminder_enabled", e.target.checked)} />
                {t("setup_reminder_enabled")}
              </label>
            </div>
            <p className="text-[11px] text-muted-foreground">{t("setup_provisional_reminder_hint")}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("setup_review_days")}</Label>
            <div className="flex items-center gap-2">
              <Input type="number" min="0" max="60" className="w-24" value={form.review_delay_days ?? 3}
                onChange={(e) => set("review_delay_days", Number(e.target.value))} data-testid="setup-review-days" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.review_enabled !== false} data-testid="setup-review-enabled"
                  onChange={(e) => set("review_enabled", e.target.checked)} />
                {t("setup_review_enabled")}
              </label>
            </div>
            <p className="text-[11px] text-muted-foreground">{t("admin_guestbook_desc")}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("setup_overdue_reminder")}</Label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.overdue_reminder_enabled !== false} data-testid="setup-overdue-reminder-enabled"
                onChange={(e) => set("overdue_reminder_enabled", e.target.checked)} />
              {t("setup_reminder_enabled")}
            </label>
            <p className="text-[11px] text-muted-foreground">{t("setup_overdue_reminder_hint")}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("setup_interim_reminder")}</Label>
            <div className="flex items-center gap-2">
              <Input type="number" min="1" max="180" className="w-24" value={form.interim_reminder_days ?? 30}
                onChange={(e) => set("interim_reminder_days", Number(e.target.value))} data-testid="setup-interim-days" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.interim_reminder_enabled !== false} data-testid="setup-interim-enabled"
                  onChange={(e) => set("interim_reminder_enabled", e.target.checked)} />
                {t("setup_reminder_enabled")}
              </label>
            </div>
            <p className="text-[11px] text-muted-foreground">{t("setup_interim_reminder_hint")}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("setup_interim_auto")}</Label>
            <div className="flex items-center gap-2">
              <Input type="number" min="1" max="180" className="w-24" value={form.interim_auto_min_days ?? 14}
                onChange={(e) => set("interim_auto_min_days", Number(e.target.value))} data-testid="setup-interim-auto-days" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.interim_auto_invoice_enabled !== false} data-testid="setup-interim-auto-enabled"
                  onChange={(e) => set("interim_auto_invoice_enabled", e.target.checked)} />
                {t("setup_reminder_enabled")}
              </label>
            </div>
            <p className="text-[11px] text-muted-foreground">{t("setup_interim_auto_hint")}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("setup_stock_alert_hour")}</Label>
            <div className="flex items-center gap-2">
              <Input type="number" min="0" max="23" className="w-24" value={form.stock_alert_hour ?? 18}
                onChange={(e) => set("stock_alert_hour", Number(e.target.value))} data-testid="setup-stock-alert-hour" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.stock_alert_enabled !== false} data-testid="setup-stock-alert-enabled"
                  onChange={(e) => set("stock_alert_enabled", e.target.checked)} />
                {t("setup_stock_alert_enabled")}
              </label>
            </div>
            <p className="text-[11px] text-muted-foreground">{t("setup_stock_alert_hint")}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("setup_audit_retention")}</Label>
            <div className="flex items-center gap-2">
              <Input type="number" min="1" max="3650" className="w-24" value={form.audit_retention_days ?? 90}
                onChange={(e) => set("audit_retention_days", Number(e.target.value))} data-testid="setup-audit-retention" />
              <span className="text-sm text-muted-foreground">{t("setup_audit_retention_unit")}</span>
            </div>
            <p className="text-[11px] text-muted-foreground">{t("setup_audit_retention_hint")}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("setup_intro_music")}</Label>
            <label className="flex items-center gap-2 text-sm h-10">
              <input type="checkbox" checked={form.intro_music !== false} data-testid="setup-intro-music"
                onChange={(e) => set("intro_music", e.target.checked)} />
              {t("setup_intro_music_on")}
            </label>
            <p className="text-[11px] text-muted-foreground">{t("setup_intro_music_hint")}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("setup_login_style")}</Label>
            <div className="flex flex-wrap gap-1.5">
              {["sober", "original", "scene3d"].map((s) => (
                <button key={s} type="button" onClick={() => set("login_style", s)} data-testid={`setup-login-style-${s}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${(form.login_style || "sober") === s ? "bg-secondary text-primary border-primary/40" : "border-border text-muted-foreground hover:bg-muted"}`}>
                  {t(`setup_login_style_${s}`)}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5 sm:col-span-2 lg:col-span-3 border-t border-border pt-4">
            <Label className="text-xs">{t("setup_display")}</Label>
            <div className="flex flex-wrap items-center gap-2">
              <Input readOnly value={displayUrl} placeholder={t("setup_display_none")}
                className="flex-1 min-w-[16rem] text-xs" data-testid="setup-display-url" />
              <Button type="button" variant="outline" size="sm" disabled={!displayUrl} data-testid="setup-display-copy"
                onClick={() => { navigator.clipboard?.writeText(displayUrl); toast.success(t("setup_display_copied")); }}>
                <Copy className="h-4 w-4 mr-1" />{t("setup_display_copy")}
              </Button>
              <Button type="button" variant="outline" size="sm" data-testid="setup-display-key"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/display/key");
                    setForm((f) => ({ ...f, display_key: data.display_key }));
                    toast.success(t("setup_display_new_ok"));
                  } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
                }}>
                <Monitor className="h-4 w-4 mr-1" />{t("setup_display_new")}
              </Button>
            </div>
            {wrongCamp && (
              <p className="text-xs font-semibold text-amber-600 dark:text-amber-400" data-testid="setup-display-mismatch">
                {t("setup_display_mismatch")
                  .replace("{work}", camp?.camp_name || camp?.tenant_id || "")
                  .replace("{host}", hostCamp?.camp_name || hostCamp?.tenant_id || "")}
              </p>
            )}

            <p className="text-[11px] text-muted-foreground">{t("setup_display_hint")}</p>
          </div>

          <div className="space-y-1.5 sm:col-span-2 lg:col-span-3 border-t border-border pt-4">
            <Label className="text-xs">{t("setup_demo")}</Label>
            {camp?.demo_allowed ? (
              <>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.demo_mode === true} data-testid="setup-demo-mode"
                    onChange={(e) => toggleDemo(e.target.checked)} />
                  {t("setup_demo_on")}
                </label>
                <p className="text-[11px] text-muted-foreground">{t("setup_demo_hint")}</p>
              </>
            ) : (
              <p className="text-[11px] text-muted-foreground">{t("setup_demo_unavailable")}</p>
            )}
          </div>


        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <LogoUpload which="main" camp={camp} onDone={reload} />
          <LogoUpload which="tent" camp={camp} onDone={reload} />
        </div>

        <div className="space-y-3 rounded-xl border border-border p-4" data-testid="setup-stationery">
          <div>
            <p className="text-sm font-semibold text-foreground">{t("setup_stationery")}</p>
            <p className="text-[11px] text-muted-foreground">{t("setup_stationery_hint")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[["bank_name", "setup_bank_name"], ["bank_account", "setup_iban"], ["bank_bic", "setup_bic"], ["footer_extra", "setup_footer_extra"]].map(([k, key]) => (
              <div key={k} className="space-y-1.5">
                <Label htmlFor={`setup-${k}`} className="text-xs">{t(key)}</Label>
                <Input id={`setup-${k}`} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`setup-${k}`} />
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-foreground">{t("setup_opening_hours")}</p>
            <HoursRows t={t} hours={hours} onChange={setHour} prefix="setup-hours" />
          </div>

          <div className="space-y-2 border-t border-border pt-3">
            <p className="text-xs font-semibold text-foreground">{t("setup_hour_seasons")}</p>
            <p className="text-[11px] text-muted-foreground">{t("setup_hour_seasons_hint")}</p>
            {seasons.map((s, i) => (
              <div key={i} className="space-y-1.5 rounded-lg border border-border p-3" data-testid={`setup-season-${i}`}>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_7rem_7rem_auto] gap-2 items-end">
                  <div className="space-y-1">
                    <Label className="text-[11px]">{t("setup_season_name")}</Label>
                    <Input value={s.name || ""} onChange={(e) => setSeason(i, "name", e.target.value)} data-testid={`setup-season-name-${i}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[11px]">{t("from")} (dd/mm)</Label>
                    <Input value={keyToDdmm(s.start)} placeholder="15/06" data-testid={`setup-season-start-${i}`}
                      onChange={(e) => setSeason(i, "start", ddmmToKey(e.target.value) || e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[11px]">{t("to")} (dd/mm)</Label>
                    <Input value={keyToDdmm(s.end)} placeholder="15/09" data-testid={`setup-season-end-${i}`}
                      onChange={(e) => setSeason(i, "end", ddmmToKey(e.target.value) || e.target.value)} />
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => set("hour_seasons", seasons.filter((_, idx) => idx !== i))} data-testid={`setup-season-del-${i}`}>
                    <Trash2 className="h-4 w-4 text-rose-500" />
                  </Button>
                </div>
                <HoursRows t={t} hours={s.hours?.length === 7 ? s.hours : EMPTY_HOURS}
                  onChange={(d, k, v) => setSeasonHour(i, d, k, v)} prefix={`setup-season-${i}-hours`} />
              </div>
            ))}
            <Button variant="outline" size="sm" data-testid="setup-season-add"
              onClick={() => set("hour_seasons", [...seasons, { name: "", start: "", end: "", hours: EMPTY_HOURS }])}>
              <Plus className="h-4 w-4 mr-1" />{t("add")}
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-semibold text-foreground">{t("setup_emergency")}</p>
          <div className="space-y-2">
            {emergency.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center" data-testid={`setup-emergency-row-${i}`}>
                <Input value={r.label || ""} placeholder={printInfo(lang).contacts[r.key] || t("setup_label")} onChange={(e) => setEmergency(i, "label", e.target.value)} data-testid={`setup-emergency-label-${i}`} />
                <Input value={r.number || ""} placeholder={t("setup_number")} onChange={(e) => setEmergency(i, "number", e.target.value)} data-testid={`setup-emergency-number-${i}`} />
                <Button variant="ghost" size="sm" onClick={() => set("emergency", emergency.filter((_, idx) => idx !== i))} data-testid={`setup-emergency-del-${i}`}>
                  <Trash2 className="h-4 w-4 text-rose-500" />
                </Button>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => set("emergency", [...emergency, { key: "", label: "", number: "" }])} data-testid="setup-emergency-add">
            <Plus className="h-4 w-4 mr-1" />{t("add")}
          </Button>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-foreground mr-2">{t("setup_info_items")}</p>
            {DOC_LANGS.map((l) => (
              <button key={l} type="button" onClick={() => setInfoLang(l)} data-testid={`setup-info-lang-${l}`}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${infoLang === l ? "bg-secondary text-primary border-primary/40" : "border-border text-muted-foreground hover:bg-muted"}`}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="space-y-3">
            {infoItems.map((row, i) => (
              <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-start" data-testid={`setup-info-row-${i}`}>
                <Input value={row[0]} onChange={(e) => setInfo(i, 0, e.target.value)} data-testid={`setup-info-title-${i}`} />
                <Textarea rows={2} value={row[1]} onChange={(e) => setInfo(i, 1, e.target.value)} data-testid={`setup-info-body-${i}`} />
                <Button variant="ghost" size="sm" onClick={() => set("info_items", { ...(form.info_items || {}), [infoLang]: infoItems.filter((_, idx) => idx !== i) })} data-testid={`setup-info-del-${i}`}>
                  <Trash2 className="h-4 w-4 text-rose-500" />
                </Button>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => set("info_items", { ...(form.info_items || {}), [infoLang]: [...infoItems, ["", ""]] })} data-testid="setup-info-add">
            <Plus className="h-4 w-4 mr-1" />{t("add")}
          </Button>
        </div>
      </div>
    </Panel>
  );
}
